/**
 * AdminContestRankingPage — Cutest Pluck close + ranking admin.
 *
 * Route: `/admin/contest/ranking`. Two-panel flow:
 *   1. Pre-close: single "Close voting" button. Confirms via modal,
 *      then POSTs `/api/admin/contest/close` which stamps
 *      `voting_close_at=now` and returns the top-10 pre-fill list.
 *   2. Post-close: renders the pre-fill list with per-row rank
 *      inputs + Save + Revoke actions.
 *
 * Per-row states:
 *   - Unranked: rank input (1..10), Save button → POST /rank.
 *   - Ranked: shows current winner_rank + ledger id + Revoke button.
 *   - Rank input `rank_slot_taken` 400 → inline row error naming
 *     the current holder.
 *
 * Post-rank toast shows "Prize credited (ledger id …)" on `prize_credited=true`
 * or "Rank updated" on idempotent no-op.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Trophy, XCircle, CheckCircle2 } from "lucide-react";
import AdminContestTabs from "@/components/admin/AdminContestTabs";
// Feature C (2026-07-21) — no more hardcoded CONTEST_ID import on
// pages/**. The backend endpoints default the `contest_id` query
// param to the active round's contest_id via `get_active_round_or_seed`.

function ConfirmCloseDialog({ open, onClose, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="admin-contest-close-confirm"
    >
      <div className="max-w-md rounded-3xl bg-pp-surface p-6 shadow-xl">
        <h2 className="font-heading text-xl font-bold text-pp-text">
          Close voting?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This stamps `voting_close_at` to now and unlocks the ranking
          flow. It's idempotent — you can safely re-run.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="rounded-full"
            data-testid="admin-contest-close-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
            data-testid="admin-contest-close-confirm-button"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Yes, close voting
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminContestRankingPage() {
  const [cfg, setCfg] = useState(null);
  const [prefill, setPrefill] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  // Per-row: draft rank + inline error + in-flight guard.
  const [rowDrafts, setRowDrafts] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [rowFlight, setRowFlight] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await api.get("/admin/contest/config");
      setCfg(c.data);
      // If voting_close_at already exists — even if in the future —
      // we're "post-close" from the admin's mental model: the close
      // has been scheduled. But the ranking guard uses "in past". So
      // to render the ranking table we probe the close endpoint,
      // which returns the prefill either way.
      const closed = c.data?.voting_close_at &&
        new Date(c.data.voting_close_at).getTime() <= Date.now();
      if (closed) {
        const r = await api.post("/admin/contest/close");
        setPrefill(r.data?.prefill_ranking || []);
        // Seed the drafts with the current winner_rank OR the rank_hint.
        const seed = {};
        for (const row of r.data?.prefill_ranking || []) {
          seed[row.entry_id] = String(row.current_winner_rank || row.rank_hint);
        }
        setRowDrafts(seed);
      } else {
        setPrefill([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closed =
    cfg?.voting_close_at &&
    new Date(cfg.voting_close_at).getTime() <= Date.now();

  async function handleClose() {
    setClosing(true);
    try {
      const r = await api.post("/admin/contest/close");
      setPrefill(r.data?.prefill_ranking || []);
      const seed = {};
      for (const row of r.data?.prefill_ranking || []) {
        seed[row.entry_id] = String(row.current_winner_rank || row.rank_hint);
      }
      setRowDrafts(seed);
      // Refresh config so the "closed" gate flips.
      await load();
      toast.success("Voting closed. Ranking is unlocked.");
      setConfirmOpen(false);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Close failed.",
      );
    } finally {
      setClosing(false);
    }
  }

  async function handleAssign(entryId) {
    const raw = rowDrafts[entryId];
    const rank = parseInt(raw, 10);
    if (!Number.isFinite(rank) || rank < 1 || rank > 10) {
      setRowErrors((s) => ({ ...s, [entryId]: "Rank must be 1..10." }));
      return;
    }
    setRowFlight((s) => ({ ...s, [entryId]: "assign" }));
    setRowErrors((s) => ({ ...s, [entryId]: "" }));
    try {
      const r = await api.post(`/admin/contest/entries/${entryId}/rank`, {
        rank,
      });
      const first = r.data?.prize_credited;
      const ledgerId = r.data?.prize_ledger_id;
      toast.success(
        first
          ? `Prize credited (ledger ${ledgerId?.slice(0, 8)}…)`
          : `Rank ${rank} confirmed (credit already issued)`,
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.error : null;
      if (code === "rank_slot_taken" && detail?.current_holder) {
        setRowErrors((s) => ({
          ...s,
          [entryId]: `Rank taken by ${detail.current_holder.dog_name}. Revoke first.`,
        }));
      } else {
        toast.error(formatApiErrorDetail(detail) || "Assign failed.");
      }
    } finally {
      setRowFlight((s) => {
        const { [entryId]: _, ...rest } = s;
        return rest;
      });
    }
  }

  async function handleRevoke(entryId) {
    setRowFlight((s) => ({ ...s, [entryId]: "revoke" }));
    try {
      const r = await api.delete(`/admin/contest/entries/${entryId}/rank`);
      const ledgerId = r.data?.ledger_id;
      toast.success(
        `Rank revoked. Credit retained (ledger ${
          ledgerId ? ledgerId.slice(0, 8) + "…" : "n/a"
        })`,
      );
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Revoke failed.",
      );
    } finally {
      setRowFlight((s) => {
        const { [entryId]: _, ...rest } = s;
        return rest;
      });
    }
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
        data-testid="admin-contest-ranking-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-contest-ranking-page">
      <AdminContestTabs />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-pp-text">
            Cutest Pluck ranking
          </h1>
          <p className="text-sm text-muted-foreground">
            Close voting, then assign ranks 1–10 from the pre-fill.
          </p>
        </div>
        {!closed && (
          <Button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
            data-testid="admin-contest-close-open"
          >
            Close voting
          </Button>
        )}
        {closed && (
          <span
            className="inline-flex items-center gap-2 rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold text-pp-text"
            data-testid="admin-contest-closed-badge"
          >
            <Trophy className="h-3 w-3" aria-hidden="true" />
            Voting closed
          </span>
        )}
      </div>

      {!closed && (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-10 text-center text-sm text-muted-foreground"
          data-testid="admin-contest-ranking-not-closed"
        >
          Close voting to see the pre-fill ranking.
        </div>
      )}

      {closed && prefill.length === 0 && (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-10 text-center text-sm text-muted-foreground"
          data-testid="admin-contest-ranking-empty"
        >
          No approved entries to rank yet.
        </div>
      )}

      {closed && prefill.length > 0 && (
        <div
          className="space-y-3"
          data-testid="admin-contest-ranking-list"
        >
          {prefill.map((row) => {
            const flight = rowFlight[row.entry_id];
            const ranked = Boolean(row.current_winner_rank);
            const err = rowErrors[row.entry_id];
            return (
              <div
                key={row.entry_id}
                className="flex flex-col gap-4 rounded-2xl border border-border bg-pp-surface p-5 sm:flex-row"
                data-testid={`admin-contest-rank-row-${row.entry_id}`}
              >
                <img
                  src={row.photo_url}
                  alt={row.dog_name}
                  className="h-24 w-24 flex-shrink-0 rounded-2xl object-cover"
                />
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3
                      className="font-heading text-lg font-bold text-pp-text"
                      data-testid={`admin-contest-rank-name-${row.entry_id}`}
                    >
                      {row.dog_name}
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {row.vote_count} votes · rank hint {row.rank_hint}
                    </span>
                    {ranked && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-pp-surface-2 px-2 py-0.5 text-xs font-semibold text-brand-orange"
                        data-testid={`admin-contest-rank-current-${row.entry_id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Rank {row.current_winner_rank}
                      </span>
                    )}
                  </div>
                  {row.caption && (
                    <p className="text-sm text-muted-foreground">
                      {row.caption}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Label
                      htmlFor={`rank-input-${row.entry_id}`}
                      className="text-sm font-semibold text-pp-text"
                    >
                      Rank
                    </Label>
                    <Input
                      id={`rank-input-${row.entry_id}`}
                      type="number"
                      min={1}
                      max={10}
                      value={rowDrafts[row.entry_id] || ""}
                      onChange={(e) =>
                        setRowDrafts((s) => ({
                          ...s,
                          [row.entry_id]: e.target.value,
                        }))
                      }
                      className="h-10 w-20 rounded-full border-border bg-pp-surface px-4"
                      data-testid={`admin-contest-rank-input-${row.entry_id}`}
                    />
                    <Button
                      type="button"
                      onClick={() => handleAssign(row.entry_id)}
                      disabled={flight != null}
                      className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
                      data-testid={`admin-contest-rank-assign-${row.entry_id}`}
                    >
                      {flight === "assign" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      {ranked ? "Update" : "Assign"}
                    </Button>
                    {ranked && (
                      <Button
                        type="button"
                        onClick={() => handleRevoke(row.entry_id)}
                        variant="secondary"
                        disabled={flight != null}
                        className="rounded-full"
                        data-testid={`admin-contest-rank-revoke-${row.entry_id}`}
                      >
                        {flight === "revoke" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="mr-2 h-4 w-4" />
                        )}
                        Revoke
                      </Button>
                    )}
                  </div>
                  {err && (
                    <p
                      className="text-xs text-brand-orange"
                      data-testid={`admin-contest-rank-error-${row.entry_id}`}
                    >
                      {err}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmCloseDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleClose}
        busy={closing}
      />
    </div>
  );
}
